import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { CopyPayLink } from '@/shared/components/billing/CopyPayLink';
import { buildPayUrl } from '@/shared/lib/payLinkMessage';

/** States a pay link is meaningful in. Mirrors payfast-public-initiate's own
 *  PAYABLE_STATES — that EF is the authority and will 409 anything else. */
const PAYABLE_FOR_LINK = new Set(['pending_payment', 'unpaid', 'grace', 'suspended']);

/** "2026-07-31" or an ISO timestamp -> "31 July 2026" for customer-facing copy. */
function formatLongDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
}
import { Receipt, MailWarning, Repeat } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Card,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  PeriodFilter,
  SearchField,
  StatusBadge,
  TABLE_PRESETS,
  UniversalFilterBar,
  useToast,
  type StatusTone,
} from '@sighthost/ui';
import {
  adminBillingService,
  type AdminInvoiceRow,
} from '@/domains/billing/services/adminBillingService';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import type { ListWindow } from '@/shared/lib/listWindow';
import { formatDate, formatZAR } from '@/shared/utils/format';
import { daysUntil } from '@/shared/utils/sastDate';

const STATE_TONE: Record<string, StatusTone> = {
  draft: 'draft',
  // 7 live documents sit in `pending_payment` (pay-first orders awaiting their
  // ITN). It was in neither map, so they rendered as 'draft' and no filter
  // could reach them.
  pending_payment: 'pending',
  unpaid: 'pending',
  grace: 'expiring',
  paid: 'active',
  suspended: 'overdue',
  refunded: 'info',
  cancelled: 'draft',
};

const KIND_LABEL: Record<string, string> = {
  hosting_renewal: 'Hosting',
  hosting_provisioning: 'Hosting setup',
  domain_registration: 'Domain new',
  domain_renewal: 'Domain renewal',
  plan_change: 'Plan change',
  one_off: 'One-off',
};

/**
 * What happens to this document next, and when — F-17.
 *
 * Every value here is already on the row; none of it was being shown. The
 * wording states dates, never outcomes: `grace_until` is when grace ends, which
 * is a fact, whereas "suspends on" would be a claim about dunning that depends
 * on `customer_link.dunning_policy` (not carried here, and currently set to an
 * exempting value on every live account).
 */
function nextStepOf(r: AdminInvoiceRow): { label: string | null; at: string | null; urgent: boolean } {
  if (r.doc_type === 'quotation') {
    const d = daysUntil(r.quote_expires_at);
    if (d === null) return { label: 'Awaiting acceptance', at: null, urgent: false };
    if (d < 0) return { label: 'Validity lapsed', at: r.quote_expires_at, urgent: true };
    return {
      label: d === 0 ? 'Valid until today' : `Valid ${d}d more`,
      at: r.quote_expires_at,
      urgent: d <= 3,
    };
  }
  switch (r.lifecycle_state) {
    case 'grace': {
      const d = daysUntil(r.grace_until);
      return d === null
        ? { label: 'In grace', at: null, urgent: true }
        : { label: 'Grace ends', at: r.grace_until, urgent: d <= 3 };
    }
    case 'suspended':
      return { label: 'Suspended since', at: r.suspended_at, urgent: true };
    case 'unpaid':
    case 'pending_payment': {
      const d = daysUntil(r.due_date);
      if (d === null) return { label: 'Due', at: r.due_date, urgent: false };
      return d < 0
        ? { label: `${Math.abs(d)}d overdue`, at: r.due_date, urgent: true }
        : { label: r.is_auto_paid ? 'Collects on' : 'Due', at: r.due_date, urgent: d <= 3 };
    }
    case 'paid':
      return { label: 'Settled', at: r.paid_at, urgent: false };
    default:
      return { label: null, at: null, urgent: false };
  }
}

type StateFilter =
  | 'all'
  | 'pending_payment'
  | 'unpaid'
  | 'grace'
  | 'paid'
  | 'suspended'
  | 'refunded'
  | 'cancelled';
type KindFilter = 'all' | 'hosting_renewal' | 'domain_registration' | 'domain_renewal' | 'plan_change' | 'one_off';
type PayModeFilter = 'all' | 'auto_pay' | 'manual';
type DocTypeFilter = 'all' | 'quotation' | 'invoice';

const STATE_OPTIONS: { value: StateFilter; label: string }[] = [
  { value: 'all', label: 'All states' },
  { value: 'pending_payment', label: 'Awaiting payment' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'grace', label: 'In grace' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'cancelled', label: 'Cancelled' },
];

const KIND_OPTIONS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'All kinds' },
  { value: 'hosting_renewal', label: 'Hosting' },
  { value: 'domain_registration', label: 'Domain new' },
  { value: 'domain_renewal', label: 'Domain renewal' },
  { value: 'plan_change', label: 'Plan change' },
  { value: 'one_off', label: 'One-off' },
];

const PAY_MODE_OPTIONS: { value: PayModeFilter; label: string }[] = [
  { value: 'all', label: 'All pay modes' },
  { value: 'auto_pay', label: 'Auto-pay only' },
  { value: 'manual', label: 'Manual only' },
];

const DOC_TYPE_OPTIONS: { value: DocTypeFilter; label: string }[] = [
  { value: 'all', label: 'All documents' },
  { value: 'quotation', label: 'Quotations' },
  { value: 'invoice', label: 'Invoices' },
];

export default function AdminInvoicesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [win, setWin] = useState<ListWindow<AdminInvoiceRow>>({
    rows: [],
    truncated: false,
    limit: 500,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [payModeFilter, setPayModeFilter] = useState<PayModeFilter>('all');
  const [docTypeFilter, setDocTypeFilter] = useState<DocTypeFilter>('all');
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderPreview, setReminderPreview] = useState<Awaited<ReturnType<typeof adminBillingService.sendBulkReminders>> | null>(null);
  const [reminderBusy, setReminderBusy] = useState(false);

  async function previewReminders() {
    setReminderBusy(true);
    setReminderPreview(null);
    try {
      const r = await adminBillingService.sendBulkReminders({ dryRun: true });
      setReminderPreview(r);
      setReminderModalOpen(true);
    } catch (e) {
      toast({ tone: 'danger', title: 'Could not preview reminders', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setReminderBusy(false);
    }
  }

  async function confirmSendReminders() {
    setReminderBusy(true);
    try {
      const r = await adminBillingService.sendBulkReminders({ dryRun: false });
      toast({
        tone: r.failed > 0 ? 'warning' : 'success',
        title: 'Reminders queued',
        body: `${r.succeeded} sent, ${r.failed} failed (of ${r.target_count}).`,
      });
      setReminderModalOpen(false);
      setReminderPreview(null);
    } catch (e) {
      toast({ tone: 'danger', title: 'Send failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setReminderBusy(false);
    }
  }

  /**
   * The four facets are predicates over the fetched rows, unchanged in meaning.
   * The PERIOD is not — it is server-side (AR-5), so it is absent from this
   * list and present in the effect's dependencies instead.
   */
  const table = useTableFilters<AdminInvoiceRow>({
    rows: win.rows,
    dateMode: 'server',
    dateLabel: 'Issued',
    searchText: (r) =>
      [r.number, r.quote_number ?? '', r.customer_name ?? '', r.customer_email ?? ''].join(' '),
    facets: useMemo(
      () => [
        (r: AdminInvoiceRow) => stateFilter === 'all' || r.lifecycle_state === stateFilter,
        (r: AdminInvoiceRow) => kindFilter === 'all' || r.invoice_kind === kindFilter,
        (r: AdminInvoiceRow) =>
          payModeFilter === 'all' ||
          (payModeFilter === 'auto_pay' ? r.is_auto_paid : !r.is_auto_paid),
        (r: AdminInvoiceRow) => docTypeFilter === 'all' || r.doc_type === docTypeFilter,
      ],
      [stateFilter, kindFilter, payModeFilter, docTypeFilter],
    ),
    truncated: win.truncated,
    limit: win.limit,
  });

  const { start: rangeStart, end: rangeEnd } = table.range;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminBillingService
      // AR-5: the period goes to the database. `issue_date DESC LIMIT 500` is a
      // truncating window; filtering it in the browser would give a "Last year"
      // control that cannot see past the 500 most recent documents.
      .listInvoices({ range: { start: rangeStart, end: rangeEnd } })
      .then((data) => {
        if (!cancelled) {
          setWin(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rangeStart, rangeEnd]);

  const filtered = table.rows;

  /**
   * F-19. These are sums over the FILTERED rows, not over the book — set the
   * state filter to "Paid" and "Unpaid" drops to zero. Only one of the four
   * cards disclosed that, in a parenthetical on the last one; now the caption
   * above the strip says it once, for all of them.
   *
   * Two correctness fixes at the same time:
   *  - `pending_payment` was in none of the buckets, so R158 of live invoices
   *    (pay-first orders awaiting their ITN) was money the page could not see.
   *    It is now counted as awaiting payment, which is what it is.
   *  - Quotations were summed into the same receivable buckets as invoices.
   *    A quotation is an offer, not a debt — R1,237 of open quotations was
   *    being presented as though someone owed it. They get their own tile.
   */
  const totals = useMemo(() => {
    const out = { awaiting: 0, grace: 0, suspended: 0, paid: 0, quotes: 0, quoteCount: 0 };
    for (const r of filtered) {
      if (r.doc_type === 'quotation') {
        if (r.lifecycle_state === 'pending_payment' || r.lifecycle_state === 'unpaid') {
          out.quotes += r.total;
          out.quoteCount += 1;
        }
        continue;
      }
      if (r.lifecycle_state === 'unpaid' || r.lifecycle_state === 'pending_payment') out.awaiting += r.total;
      if (r.lifecycle_state === 'grace') out.grace += r.total;
      if (r.lifecycle_state === 'suspended') out.suspended += r.total;
      if (r.lifecycle_state === 'paid') out.paid += r.total;
    }
    return out;
  }, [filtered]);

  const filtersActive =
    stateFilter !== 'all' ||
    kindFilter !== 'all' ||
    payModeFilter !== 'all' ||
    docTypeFilter !== 'all' ||
    table.q.trim().length > 0 ||
    table.filters.activeCount > 0;

  const columns = useMemo<UniversalTableColumn<AdminInvoiceRow>[]>(
    () => [
      {
        key: 'number',
        header: 'Invoice',
        sortValue: (r) => r.number,
        render: (r) => (
          <div className="flex items-center gap-1.5">
            <code className="text-sm text-foreground">{r.number}</code>
            {r.doc_type === 'quotation' && (
              <span
                className="inline-flex items-center rounded bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-warning-dark"
                title={
                  r.quote_expires_at
                    ? `Quotation — valid until ${formatDate(r.quote_expires_at)}`
                    : 'Quotation — converts to an invoice when paid'
                }
              >
                Quote
              </span>
            )}
            {r.doc_type === 'invoice' && r.quote_number && (
              <span
                className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-foreground-soft"
                title={`Converted from quotation ${r.quote_number}`}
              >
                Was {r.quote_number}
              </span>
            )}
            {r.is_auto_paid && (
              <span
                className="inline-flex items-center gap-0.5 rounded bg-success/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-success"
                title="Auto-paid via PayFast Recurring cycle"
              >
                <Repeat className="size-2.5" aria-hidden />
                Auto
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'customer',
        header: 'Customer',
        sortValue: (r) => r.customer_name ?? r.customer_email ?? '',
        render: (r) => (
          <div className="flex flex-col">
            <span className="text-foreground">{r.customer_name ?? '—'}</span>
            {r.customer_email && (
              <span className="text-xs text-muted-foreground">{r.customer_email}</span>
            )}
          </div>
        ),
      },
      {
        key: 'kind',
        header: 'Kind',
        sortValue: (r) => r.invoice_kind,
        render: (r) => (
          <span className="text-sm text-foreground">{KIND_LABEL[r.invoice_kind] ?? r.invoice_kind}</span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'issue',
        header: 'Issued',
        sortValue: (r) => new Date(r.issue_date).getTime(),
        render: (r) => <span className="text-foreground">{formatDate(r.issue_date)}</span>,
        hideOnMobile: true,
      },
      {
        key: 'due',
        header: 'Due',
        sortValue: (r) => new Date(r.due_date).getTime(),
        render: (r) => <span className="text-foreground">{formatDate(r.due_date)}</span>,
      },
      {
        key: 'total',
        header: 'Total',
        align: 'right',
        sortValue: (r) => r.total,
        render: (r) => <span className="font-semibold text-foreground">{formatZAR(r.total)}</span>,
      },
      {
        key: 'state',
        header: 'State',
        sortValue: (r) => r.lifecycle_state,
        render: (r) => <StatusBadge tone={STATE_TONE[r.lifecycle_state] ?? 'draft'}>{r.lifecycle_state}</StatusBadge>,
        width: 'w-32',
      },
      {
        // F-17. The state badge named where a document IS; nothing on the row
        // said what happens next or when — and `grace_until` / `quote_expires_at`
        // were already being fetched and thrown away. Reading the list meant
        // opening rows to find out which deadline was nearest.
        //
        // Deliberately says "Grace ends" and not "Suspends on": whether dunning
        // acts is a property of `customer_link.dunning_policy`, which this row
        // does not carry, and every live account is currently on a policy that
        // exempts it (CLAUDE.md 2026-08-18). Stating the date is a fact; naming
        // the consequence would be a guess.
        key: 'next',
        header: 'Next step',
        sortValue: (r) => {
          const at = nextStepOf(r).at;
          return at ? new Date(at).getTime() : Infinity;
        },
        render: (r) => {
          const step = nextStepOf(r);
          if (!step.label) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="flex flex-col">
              <span className={step.urgent ? 'text-sm font-medium text-warning-dark' : 'text-sm text-foreground'}>
                {step.label}
              </span>
              {step.at && <span className="text-xs text-muted-foreground">{formatDate(step.at)}</span>}
            </div>
          );
        },
        hideOnMobile: true,
      },
      {
        // Copy-pay-link, admin/support tier (2026-07-27). Dense variant: link +
        // WhatsApp only, because a table row cannot carry three more buttons.
        // Renders only while the document is genuinely payable, so a settled
        // invoice never offers a dead link.
        key: 'paylink',
        header: 'Send link',
        align: 'right',
        render: (r) =>
          PAYABLE_FOR_LINK.has(r.lifecycle_state) && r.payment_token ? (
            <CopyPayLink
              dense
              payUrl={buildPayUrl(r.id, r.payment_token)}
              autopayUrl={
                r.autopay_eligible
                  ? buildPayUrl(r.id, r.payment_token, { autopay: true })
                  : null
              }
              reference={r.number}
              docLabel={r.doc_type === 'quotation' ? 'quotation' : 'invoice'}
              amountZar={r.total}
              customerName={r.customer_name}
              dueLabel={formatLongDate(
                r.doc_type === 'quotation' ? r.quote_expires_at ?? r.due_date : r.due_date
              )}
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Billing"
      breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Billing' }]}
      actions={
        <Button variant="outline" size="sm" onClick={previewReminders} disabled={reminderBusy} loading={reminderBusy}>
          {reminderBusy ? null : <MailWarning className="h-4 w-4" />}
          Send overdue reminders
        </Button>
      }
    >
      <div>
        <p className="mb-2 text-xs text-muted-foreground">
          {loading
            ? 'Totalling…'
            : filtersActive
              ? `Totals over the ${filtered.length} document${filtered.length === 1 ? '' : 's'} matching your filters — not the whole book.`
              : `Totals over all ${filtered.length} document${filtered.length === 1 ? '' : 's'} in view${table.isTruncated ? ` (capped at ${win.limit} — more exist)` : ''}.`}
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Card interactive={false} className="p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Awaiting payment</p>
            <p className="mt-1 text-lg font-semibold text-foreground">{formatZAR(totals.awaiting)}</p>
          </Card>
          <Card interactive={false} className="p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">In grace</p>
            <p className="mt-1 text-lg font-semibold text-warning-dark">{formatZAR(totals.grace)}</p>
          </Card>
          <Card interactive={false} className="p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Suspended</p>
            <p className="mt-1 text-lg font-semibold text-danger">{formatZAR(totals.suspended)}</p>
          </Card>
          <Card interactive={false} className="p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Paid</p>
            <p className="mt-1 text-lg font-semibold text-success">{formatZAR(totals.paid)}</p>
          </Card>
          <Card interactive={false} className="p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Open quotations</p>
            <p className="mt-1 text-lg font-semibold text-foreground">{formatZAR(totals.quotes)}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {totals.quoteCount} offer{totals.quoteCount === 1 ? '' : 's'} — not receivable
            </p>
          </Card>
        </div>
      </div>

      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as StateFilter)}
            className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {STATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KindFilter)}
            className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={payModeFilter}
            onChange={(e) => setPayModeFilter(e.target.value as PayModeFilter)}
            className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            title="Auto-pay = paid automatically by a PayFast Recurring cycle (hosting, Care, Studio installment)"
          >
            {PAY_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={docTypeFilter}
            onChange={(e) => setDocTypeFilter(e.target.value as DocTypeFilter)}
            className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            title="Quotations carry QUO- numbers and convert to invoices in place when paid"
          >
            {DOC_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search number, customer, email"
            className="ml-auto w-full max-w-xs"
          />
        </div>

        {/* The period sits with the facets it composes with — proximity (AR-4). */}
        <div className="mt-3 border-t border-border pt-3">
          <UniversalFilterBar
            bordered={false}
            period={
              <PeriodFilter
                value={table.filters.period}
                from={table.filters.from}
                to={table.filters.to}
                onChange={table.filters.setPeriod}
                onStep={table.stepPeriodBy}
                presets={TABLE_PRESETS}
                variant="menu"
                steppable
                label="Issued"
              />
            }
            activeCount={table.filters.activeCount + (table.q ? 1 : 0)}
            onClear={() => {
              // D-1 (QA, 2026-09-13): Clear must clear the SEARCH too. It reset
              // only the period, so a user pressed Clear and the table stayed
              // filtered by a term they had just been told was gone — and the
              // bar could read "1 filter" while two were applied.
              table.filters.clear();
              table.setQ('');
            }}
          />
        </div>
      </Card>

      <UniversalTable<AdminInvoiceRow>
        data={filtered}
        columns={columns}
        getRowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/admin/customers/${r.customer_id}/billing`)}
        isLoading={loading}
        error={error}
        /* Rule T-4 + AR-6. */
        caption={loading ? undefined : table.caption}
        empty={{
          icon: Receipt,
          title: table.totalCount === 0 ? 'Nothing in this period' : 'No matches',
          description:
            table.totalCount === 0
              ? table.filters.activeCount > 0
                ? 'No documents were issued in the selected period. Widen it, or choose All time.'
                : 'Invoices appear here once the renewal cron issues the first one or staff issues a manual invoice.'
              : 'Try a different state, kind, or search term.',
        }}
      />

      <Modal
        open={reminderModalOpen}
        onOpenChange={(v) => !v && setReminderModalOpen(false)}
        title="Send overdue payment reminders"
        description={`Targets invoices in 'grace' or 'suspended' state past due_date. Same-day re-runs are no-ops.`}
        size="wide"
      >
        <ModalBody>
          {reminderPreview ? (
            reminderPreview.target_count === 0 ? (
              <p className="text-sm text-muted-foreground">
                No overdue invoices match — nothing to send.
              </p>
            ) : (
              <>
                <p className="text-sm text-foreground">
                  <strong>{reminderPreview.target_count}</strong> invoice{reminderPreview.target_count === 1 ? '' : 's'} would receive a reminder email
                  using the <code>invoice_grace_started</code> template.
                </p>
                <ul className="mt-3 max-h-64 overflow-y-auto space-y-1 text-xs">
                  {(reminderPreview.previewed ?? []).slice(0, 50).map((p) => (
                    <li key={p.invoice_number} className="flex justify-between gap-3 rounded bg-muted px-2 py-1 tabular-nums">
                      <span>{p.invoice_number}</span>
                      <span>{p.due_date}</span>
                      <span>R{p.total.toFixed(2)}</span>
                    </li>
                  ))}
                  {(reminderPreview.previewed ?? []).length > 50 && (
                    <li className="px-2 py-1 text-muted-foreground">
                      …and {(reminderPreview.previewed ?? []).length - 50} more.
                    </li>
                  )}
                </ul>
              </>
            )
          ) : (
            <p className="text-sm text-muted-foreground">Computing preview…</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setReminderModalOpen(false)} disabled={reminderBusy}>
            Cancel
          </Button>
          <Button
            onClick={confirmSendReminders}
            disabled={reminderBusy || !reminderPreview || reminderPreview.target_count === 0}
            loading={reminderBusy}
          >
            <MailWarning className="h-4 w-4" />
            Send {reminderPreview ? reminderPreview.target_count : ''} reminder{reminderPreview && reminderPreview.target_count === 1 ? '' : 's'}
          </Button>
        </ModalFooter>
      </Modal>
    </UniversalPage>
  );
}
